import React, { useState } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogTrigger,
    Button,
    makeStyles,
    tokens
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { Alert, UpdateAlertRequest } from '../Types/alert';
import AlertComponent from './Alert';
import { useSignInState } from './signInState';

interface AlertModalProps {
    alert: Alert;
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedAlert: UpdateAlertRequest) => Promise<void>;
    title?: string;
}

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: '800px',
        width: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    dialogContent: {
        padding: '24px',
        overflow: 'auto',
    },
    dialogTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: tokens.colorNeutralForeground1,
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: tokens.colorNeutralBackgroundAlpha,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    errorMessage: {
        color: tokens.colorPaletteRedForeground1,
        fontSize: '14px',
        marginTop: '8px',
    },
});

// Helper validation functions
const isEmptyValue = (value: any): boolean => {
    return !value || (typeof value === 'string' && value.trim().length === 0);
};

const isStringTooShort = (value: any, minLength: number): boolean => {
    return typeof value === 'string' && value.trim().length < minLength;
};

const validateRequiredField = (value: any, fieldName: string, minLength?: number): string[] => {
    const errors: string[] = [];
    
    if (isEmptyValue(value)) {
        errors.push(`${fieldName} is required.`);
    } else if (minLength && isStringTooShort(value, minLength)) {
        errors.push(`${fieldName} must be at least ${minLength} characters long.`);
    }
    
    return errors;
};

const validateThresholdValue = (value: any): string[] => {
    const errors: string[] = [];
    
    if (value === null || value === undefined) {
        errors.push('Threshold Value is required.');
    }
    
    return errors;
};

const parseNotificationEmails = (emailString: string): string[] => {
    return emailString
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);
};

const validateEmailFormat = (emails: string[]): string[] => {
    const errors: string[] = [];
    // Use a more secure regex that avoids catastrophic backtracking
    // This regex has linear time complexity and prevents ReDoS attacks
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
        errors.push(`Invalid email format: ${invalidEmails.join(', ')}`);
    }
    
    return errors;
};

const validateEmailDomain = (emails: string[], userEmail: string): string[] => {
    const errors: string[] = [];
    const userDomain = userEmail.split('@')[1];
    const differentDomainEmails = emails.filter(email => email.split('@')[1] !== userDomain);
    
    if (differentDomainEmails.length > 0) {
        errors.push(`All notification emails must be from the same domain as your email (${userDomain}): ${differentDomainEmails.join(', ')}`);
    }
    
    return errors;
};

const validateEmailCount = (emails: string[]): string[] => {
    const errors: string[] = [];
    
    if (emails.length === 0) {
        errors.push('At least one notification email is required.');
    } else if (emails.length > 5) {
        errors.push('Maximum of 5 notification emails allowed.');
    }
    
    return errors;
};

const validateNotificationEmails = (emailString: string, userEmail?: string): string[] => {
    const errors: string[] = [];
    
    // Check if email string is provided
    if (!emailString || emailString.trim() === '') {
        errors.push('Notification emails are required.');
        return errors;
    }
    
    // Parse emails
    const emails = parseNotificationEmails(emailString);
    
    // Check email count
    errors.push(...validateEmailCount(emails));
    
    // If count validation passed, validate format and domain
    if (errors.length === 0) {
        errors.push(...validateEmailFormat(emails));
        
        if (userEmail && errors.length === 0) {
            errors.push(...validateEmailDomain(emails, userEmail));
        }
    }
    
    return errors;
};

// Main validation function
const validateAlertData = (alertData: UpdateAlertRequest, userEmail?: string): { isValid: boolean; errors: string[] } => {
    // Validate each field using helper functions and collect all errors
    const errors = [
        ...validateRequiredField(alertData.AlertTitle, 'Alert Title', 15),
        ...validateRequiredField(alertData.ComparisonOperator, 'Comparison Operator'),
        ...validateThresholdValue(alertData.ThresholdValue),
        ...validateRequiredField(alertData.AlertSensitivity, 'Alert Sensitivity'),
        ...validateNotificationEmails(alertData.NotificationEmails || '', userEmail)
    ];

    return {
        isValid: errors.length === 0,
        errors
    };
};

const AlertModal: React.FC<AlertModalProps> = ({ 
    alert, 
    isOpen, 
    onClose, 
    onSave, 
    title = "Edit Alert" 
}) => {
    const styles = useStyles();
    const { signInState } = useSignInState();
    const [isLoading, setIsLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const handleSave = async (updatedAlert: UpdateAlertRequest) => {
        // Clear previous validation errors
        setValidationErrors([]);
        
        // Get current user's email for domain validation
        const userEmail = signInState.userInfo[0]?.Email;
        
        // Validate the alert data
        const validation = validateAlertData(updatedAlert, userEmail);
        
        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            return; // Don't proceed with save if validation fails
        }
        
        setIsLoading(true);
        try {
            await onSave(updatedAlert);
            setHasUnsavedChanges(false);
            onClose();
        } catch (error) {
            console.error('Error saving alert:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            // You might want to show a confirmation dialog here
            const confirmed = globalThis.confirm(
                'You have unsaved changes. Are you sure you want to close without saving?'
            );
            if (!confirmed) {
                return;
            }
        }
        setHasUnsavedChanges(false);
        onClose();
    };

    return (
        <Dialog 
            modalType="modal"
            open={isOpen}
            onOpenChange={(event, data) => {
                if (!data.open) {
                    handleCancel();
                }
            }}
        >
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody>
                    <DialogTitle
                        className={styles.dialogTitle}
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleCancel}
                                />
                            </DialogTrigger>
                        }
                    >
                        {title}
                    </DialogTitle>
                    <DialogContent className={styles.dialogContent}>
                        {isLoading && (
                            <div className={styles.loadingOverlay}>
                                <div>Saving changes...</div>
                            </div>
                        )}
                        {validationErrors.length > 0 && (
                            <div className={styles.errorMessage}>
                                <strong>Please fix the following errors:</strong>
                                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                                    {validationErrors.map((error) => (
                                        <li key={error}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <AlertComponent
                            alert={alert}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default AlertModal;

